import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Users, Wifi, WifiOff, MessageCircle } from 'lucide-react';
import { useEventStore } from '@/stores/eventStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { VenueTastingEvent, EventGuest } from '@/types';

interface WaitingRoomProps {
  event: VenueTastingEvent;
  guest: EventGuest;
  onEventStart: () => void;
}

export default function WaitingRoom({ event, guest, onEventStart }: WaitingRoomProps) {
  const { isEventStarted, moderatorMessage, isWebSocketConnected, setWebSocketConnected } = useEventStore();

  const { isConnected, error } = useWebSocket({
    accessCode: event.access_code,
    guestId: guest.id,
    guestName: guest.name || 'Gast',
    clientType: 'guest',
  });

  // Update store with WebSocket connection status
  useEffect(() => {
    setWebSocketConnected(isConnected);
  }, [isConnected, setWebSocketConnected]);

  // Trigger onEventStart when event starts
  useEffect(() => {
    if (isEventStarted || event.status === 'active') {
      onEventStart();
    }
  }, [isEventStarted, event.status, onEventStart]);

  // Format scheduled time
  const formatScheduledTime = () => {
    if (!event.scheduled_at) return null;
    const date = new Date(event.scheduled_at);
    return date.toLocaleString('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Event Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-display font-bold text-dark-50 mb-2">
            {event.name}
          </h1>
          {event.venue_name && (
            <p className="text-dark-400">{event.venue_name}</p>
          )}
        </div>

        {/* Main Card */}
        <motion.div
          className="card text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* Waiting Animation */}
          <div className="mb-6">
            <motion.div
              className="w-20 h-20 mx-auto bg-primary-500/20 rounded-full flex items-center justify-center"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [1, 0.8, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <Clock className="w-10 h-10 text-primary-500" />
            </motion.div>
          </div>

          <h2 className="text-xl font-semibold text-dark-100 mb-2">
            Warte auf Start...
          </h2>
          <p className="text-dark-400 text-sm mb-6">
            Das Tasting wurde noch nicht gestartet. Du wirst automatisch benachrichtigt, sobald es losgeht.
          </p>

          {/* Scheduled Time */}
          {event.scheduled_at && (
            <div className="bg-dark-800/50 rounded-lg p-4 mb-4">
              <p className="text-dark-300 text-sm">Geplanter Start:</p>
              <p className="text-dark-100 font-medium">{formatScheduledTime()}</p>
            </div>
          )}

          {/* Participant Count */}
          <div className="flex items-center justify-center gap-2 text-dark-400 text-sm mb-4">
            <Users className="w-4 h-4" />
            <span>
              {event.current_participants} von {event.max_participants} Teilnehmer
            </span>
          </div>

          {/* Connection Status */}
          <div className="flex items-center justify-center gap-2 text-sm">
            {isConnected ? (
              <>
                <Wifi className="w-4 h-4 text-success-500" />
                <span className="text-success-500">Verbunden</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-warning-500" />
                <span className="text-warning-500">
                  {error || 'Verbinde...'}
                </span>
              </>
            )}
          </div>
        </motion.div>

        {/* Moderator Message */}
        <AnimatePresence>
          {moderatorMessage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-4 p-4 bg-primary-500/10 border border-primary-500/30 rounded-xl"
            >
              <div className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-primary-300 text-sm font-medium mb-1">
                    Nachricht vom Moderator:
                  </p>
                  <p className="text-dark-200">{moderatorMessage}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Guest Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-dark-500 text-sm">
            Angemeldet als: <span className="text-dark-300">{guest.name || 'Gast'}</span>
          </p>
        </motion.div>

        {/* Spirits Preview */}
        {event.spirits && event.spirits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6"
          >
            <h3 className="text-sm font-medium text-dark-400 mb-3 text-center">
              Spirituosen in diesem Tasting:
            </h3>
            <div className="space-y-2">
              {event.spirits.map((spirit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-dark-800/30 rounded-lg"
                >
                  <span className="w-6 h-6 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 text-sm font-medium">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-dark-200 text-sm font-medium">
                      {spirit.spirit_name}
                    </p>
                    {spirit.spirit_brand && (
                      <p className="text-dark-500 text-xs">{spirit.spirit_brand}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
